import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Target, Plus, CheckCircle, RefreshCw, XCircle } from "lucide-react";

// Subject display names
const SUBJECT_NAMES = {
  math: "Mathematics",
  science: "Science",
  english: "English Grammar",
  hindi: "Hindi Vyakaran"
};

// Topics by subject
const TOPICS_BY_SUBJECT = {
  math: ["Number Systems", "Fractions & Decimals", "Algebra", "Geometry", "Mensuration", "Data Handling", "Ratio & Proportion", "Integers"],
  science: ["Physics", "Chemistry", "Biology", "Environmental Science", "Human Body", "Plants & Animals", "Matter & Energy", "Forces & Motion"],
  english: ["Grammar", "Reading Comprehension", "Writing Skills", "Vocabulary", "Literature", "Poetry", "Creative Writing", "Communication"],
  hindi: ["व्याकरण", "पठन", "लेखन", "शब्द भंडार", "साहित्य", "कविता", "निबंध", "पत्र लेखन"]
};

export function TopicSetupModal({
  open,
  onOpenChange,
  selectedChild,
  activeTopicSubject,
  setActiveTopicSubject,
  selectedTopics,
  setSelectedTopics,
  customTopics,
  setCustomTopics,
  newCustomTopic,
  setNewCustomTopic,
  topicSetupLoading,
  onSave
}) {
  if (!selectedChild) return null;
  
  const subjects = selectedChild.subjects_required?.split(',').map(s => s.trim().toLowerCase().replace(' ', '_').replace('social studies', 'social_studies')) || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-blue-600" />
            Configure Learning Topics for {selectedChild?.name}
          </DialogTitle>
          <DialogDescription>
            Select specific topics for each subject to help our AI deliver focused learning and track progress effectively.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Subject Tabs */}
          <div className="flex flex-wrap gap-2 border-b pb-3">
            {subjects.map(sub => (
              <Button
                key={sub}
                variant={activeTopicSubject === sub ? "default" : "outline"}
                size="sm"
                onClick={() => setActiveTopicSubject(sub)}
                className={activeTopicSubject === sub ? "bg-blue-600" : ""}
              >
                {SUBJECT_NAMES[sub] || sub}
                {selectedTopics[sub]?.length > 0 && (
                  <span className="ml-1.5 bg-white/20 text-xs px-1.5 py-0.5 rounded-full">
                    {selectedTopics[sub].length}
                  </span>
                )}
              </Button>
            ))}
          </div>
          
          {/* Topic Selection for Active Subject */}
          {activeTopicSubject && (
            <div className="space-y-4">
              {/* AI Suggested Notice */}
              <div className="flex items-center gap-2 text-sm text-blue-600 bg-blue-50 p-2 rounded-lg">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Topics are suggested based on Grade {selectedChild.grade} curriculum
              </div>
              
              {/* Topic Checkboxes */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {(TOPICS_BY_SUBJECT[activeTopicSubject] || []).map(topic => {
                  const isSelected = selectedTopics[activeTopicSubject]?.includes(topic);
                  return (
                    <label
                      key={topic}
                      className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer transition-all ${
                        isSelected 
                          ? 'bg-blue-50 border-blue-400 text-blue-900' 
                          : 'bg-white border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          setSelectedTopics(prev => {
                            const current = prev[activeTopicSubject] || [];
                            if (e.target.checked) {
                              return { ...prev, [activeTopicSubject]: [...current, topic] };
                            } else {
                              return { ...prev, [activeTopicSubject]: current.filter(t => t !== topic) };
                            }
                          });
                        }}
                        className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                      <span className="text-sm">{topic}</span>
                    </label>
                  );
                })}
              </div>
              
              {/* Custom Topics */}
              <div className="border-t pt-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Add Custom Topics</p>
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter a topic not listed above..."
                    value={newCustomTopic}
                    onChange={(e) => setNewCustomTopic(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && newCustomTopic.trim()) {
                        setCustomTopics(prev => ({
                          ...prev,
                          [activeTopicSubject]: [...(prev[activeTopicSubject] || []), newCustomTopic.trim()]
                        }));
                        setNewCustomTopic("");
                      }
                    }}
                  />
                  <Button
                    variant="outline"
                    onClick={() => {
                      if (newCustomTopic.trim()) {
                        setCustomTopics(prev => ({
                          ...prev,
                          [activeTopicSubject]: [...(prev[activeTopicSubject] || []), newCustomTopic.trim()]
                        }));
                        setNewCustomTopic("");
                      }
                    }}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                
                {/* Display Custom Topics */}
                {customTopics[activeTopicSubject]?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {customTopics[activeTopicSubject].map((topic, idx) => (
                      <span
                        key={`custom-${idx}-${topic}`}
                        className="inline-flex items-center gap-1 bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-sm"
                      >
                        {topic}
                        <button
                          onClick={() => {
                            setCustomTopics(prev => ({
                              ...prev,
                              [activeTopicSubject]: prev[activeTopicSubject].filter((_, i) => i !== idx)
                            }));
                          }}
                          className="hover:text-purple-600"
                        >
                          <XCircle className="h-3.5 w-3.5" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Selection Summary */}
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm text-gray-600">
                  <strong>{SUBJECT_NAMES[activeTopicSubject]}:</strong>{" "}
                  {(selectedTopics[activeTopicSubject]?.length || 0) + (customTopics[activeTopicSubject]?.length || 0)} topics selected
                </p>
              </div>
            </div>
          )}
          
          {/* Overall Summary */}
          <div className="border-t pt-4">
            <p className="text-sm font-medium text-gray-700 mb-2">Selection Summary:</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {subjects.map(sub => {
                const count = (selectedTopics[sub]?.length || 0) + (customTopics[sub]?.length || 0);
                return (
                  <div key={sub} className={`text-sm p-2 rounded ${count > 0 ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {SUBJECT_NAMES[sub]}: {count} topics
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={topicSetupLoading}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {topicSetupLoading ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                Save Topics
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
